// file: shared/ModelVoiceSelector.jsx
// Reusable LLM model + TTS voice selector.
// Uses the same AI_MODELS and AVAILABLE_VOICES/VOICE_GROUPS constants as the
// Audio Narration section in EnhancedPOIForm so both places stay in sync.

import React from 'react';
import { Bus } from 'lucide-react';
import { AI_MODELS } from '../constants/aiConstants';
import { AVAILABLE_VOICES, VOICE_GROUPS } from '../ItineraryCreator/utils/voiceConstants';

const PROVIDER_LABELS = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google',
};

// Groups AI_MODELS by provider for the <optgroup> structure
function groupedModels() {
  const groups = {};
  for (const m of AI_MODELS) {
    const p = m.provider || 'openai';
    if (!groups[p]) groups[p] = [];
    groups[p].push(m);
  }
  return groups;
}

/**
 * ModelVoiceSelector
 *
 * Props:
 *   model          {string}   — current model id
 *   voice          {string}   — current voice key (from AVAILABLE_VOICES)
 *   onModelChange  {fn}       — (modelId: string) => void
 *   onVoiceChange  {fn}       — (voiceKey: string, voiceConfig: object) => void
 *   compact        {bool}     — renders as a tighter inline row (default: false)
 */
const ModelVoiceSelector = ({ model, voice, onModelChange, onVoiceChange, compact = false }) => {
  const modelGroups = groupedModels();

  const handleVoiceChange = (key) => {
    onVoiceChange(key, AVAILABLE_VOICES[key] || null);
  };

  const selectedVoiceConfig = AVAILABLE_VOICES[voice];

  if (compact) {
    return (
      <div className="flex flex-wrap gap-3 items-end">
        {/* Model */}
        <div className="flex-1 min-w-40">
          <label className="block text-xs font-medium text-gray-600 mb-1">LLM Model</label>
          <select
            value={model}
            onChange={e => onModelChange(e.target.value)}
            className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
          >
            {Object.entries(modelGroups).map(([provider, models]) => (
              <optgroup key={provider} label={PROVIDER_LABELS[provider] || provider}>
                {models.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.name}{m.recommended ? ' ★' : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        {/* Voice */}
        <div className="flex-1 min-w-48">
          <label className="block text-xs font-medium text-gray-600 mb-1">TTS Voice</label>
          <div className="relative">
            <Bus className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-amber-500" />
            <select
              value={voice}
              onChange={e => handleVoiceChange(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white py-1.5 pl-7 pr-2 text-xs focus:outline-none focus:ring-1 focus:ring-purple-400"
            >
              {VOICE_GROUPS.map(group => (
                <optgroup key={group.language} label={group.language}>
                  {group.voices
                    .filter(key => AVAILABLE_VOICES[key])
                    .map(key => (
                      <option key={key} value={key}>
                        {AVAILABLE_VOICES[key].name}
                      </option>
                    ))}
                </optgroup>
              ))}
            </select>
          </div>
          {selectedVoiceConfig && (
            <p className="text-[10px] text-gray-400 mt-0.5">
              {selectedVoiceConfig.provider} · {selectedVoiceConfig.languageCode}
              {selectedVoiceConfig.useSimpleTtsMethod ? ' · SimpleTTS' : ''}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Full (stacked) layout
  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Model */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1.5">LLM Model</label>
        <select
          value={model}
          onChange={e => onModelChange(e.target.value)}
          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
        >
          {Object.entries(modelGroups).map(([provider, models]) => (
            <optgroup key={provider} label={PROVIDER_LABELS[provider] || provider}>
              {models.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name}{m.recommended ? ' ★' : ''}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        {model && (() => {
          const info = AI_MODELS.find(m => m.id === model);
          return info ? (
            <p className="text-xs text-gray-400 mt-1">{info.description}</p>
          ) : null;
        })()}
      </div>

      {/* Voice */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1.5">TTS Voice</label>
        <div className="relative">
          <Bus className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-500" />
          <select
            value={voice}
            onChange={e => handleVoiceChange(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-purple-400"
          >
            {VOICE_GROUPS.map(group => (
              <optgroup key={group.language} label={group.language}>
                {group.voices
                  .filter(key => AVAILABLE_VOICES[key])
                  .map(key => (
                    <option key={key} value={key}>
                      {AVAILABLE_VOICES[key].name}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
        </div>
        {selectedVoiceConfig && (
          <p className="text-xs text-gray-400 mt-1">
            {selectedVoiceConfig.provider} · {selectedVoiceConfig.languageCode}
            {selectedVoiceConfig.useSimpleTtsMethod ? ' · SimpleTTS (highest quality)' : ''}
            {selectedVoiceConfig.model ? ` · ${selectedVoiceConfig.model}` : ''}
          </p>
        )}
      </div>
    </div>
  );
};

export default ModelVoiceSelector;
